import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { fetchAnsSus } from '@/services/ans';

const QUANTILES = ['p10', 'p25', 'p50', 'p75', 'p90'] as const;
const BANDS = ['0%', '0-25%', '25-50%', '50-75%', '75-100%', '100%'] as const;

/**
 * The SUS reimbursement index: a levy, never a sanction.
 *
 * ANS publishes, per operator and competence, the collected share of the
 * reimbursements every operator owes the public system -- as a percentage,
 * never as an amount. This page shows the shape of that ledger and the
 * dispersion of the index, and says on its face what the index is and that
 * no money can be summed from it.
 */
export function AnsSusPage() {
  const { t, i18n } = useTranslation(['ans', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ans', 'sus'],
    queryFn: fetchAnsSus,
  });
  const number = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });
  const integer = new Intl.NumberFormat(i18n.language);

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ans:sus.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ans:sus.subtitle')}</p>
      </header>

      <p className="rounded-md border border-dashed p-3 text-xs text-foreground" role="note">
        {t('ans:sus.fence')}
      </p>

      <ExplainerCard i18nKey="ans:sus.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/ans/export/sus" label={t('ans:sus.export')} />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-shape>
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('ans:sus.shape.title')}</h2>
        <Answer answer={data.shape} help={t('ans:sus.shape.help')}>
          {(s) => (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Figure label={t('ans:sus.shape.rows')} value={integer.format(s.rows)} />
              <Figure label={t('ans:sus.shape.operators')} value={integer.format(s.operators)} />
              <Figure
                label={t('ans:sus.shape.competences')}
                value={`${s.first_competence ?? '—'} – ${s.last_competence ?? '—'} (${integer.format(
                  s.competences,
                )})`}
              />
              <Figure
                label={t('ans:sus.shape.withPenalty')}
                value={integer.format(s.operators_with_penalty)}
              />
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="ans:sus.guide" />
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-by-year>
          <h2 className="mb-3 text-sm font-semibold text-foreground">{t('ans:sus.years.title')}</h2>
          <Answer answer={data.by_year} help={t('ans:sus.years.help')}>
            {(rows) => (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('ans:sus.years.year')}</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ans:sus.years.rows')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ans:sus.years.operators')}
                      </th>
                      {QUANTILES.map((q) => (
                        <th key={q} className="px-3 py-2 text-right font-medium">
                          {q}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((y) => (
                      <tr key={y.year} className="border-t" data-year={y.year}>
                        <td className="px-3 py-2 tabular-nums text-foreground">{y.year}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {integer.format(y.rows)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {integer.format(y.operators)}
                        </td>
                        {QUANTILES.map((q) => (
                          <td key={q} className="px-3 py-2 text-right tabular-nums text-foreground">
                            {y.index ? `${number.format(y.index.quantiles[q] ?? 0)}%` : '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Answer>
        </section>

        <section className="rounded-lg border bg-card p-4" data-bands>
          <h2 className="mb-3 text-sm font-semibold text-foreground">{t('ans:sus.bands.title')}</h2>
          <Answer answer={data.bands} help={t('ans:sus.bands.help')}>
            {(b) => (
              <ul className="grid grid-cols-3 gap-2">
                {BANDS.map((band) => (
                  <li key={band} className="rounded-md border bg-muted/30 p-2" data-band={band}>
                    <div className="text-xs text-muted-foreground">{band}</div>
                    <div className="font-semibold tabular-nums text-foreground">
                      {integer.format(b.bands[band] ?? 0)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Answer>
        </section>
      </div>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-base font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}
